import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';

const DISCORD_INVITE_URL = process.env.REACT_APP_DISCORD_INVITE;

/**
 * Route wrapper that requires user to be approved for beta access
 * Shows approval message if not approved, redirects to landing if not logged in
 */
const BetaApprovedRoute = ({ children }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isApproved, setIsApproved] = useState(false);
  const [isAuth, setIsAuth] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user || !user.emailVerified) {
        setIsAuth(false);
        setIsApproved(false);
        setLoading(false);
        return;
      }

      setIsAuth(true);

      try {
        // Check if user is superadmin first (superadmins bypass beta approval)
        let isSuperAdmin = false;
        try {
          const tokenResult = await user.getIdTokenResult();
          if (tokenResult?.claims?.superadmin) {
            isSuperAdmin = true;
          }
        } catch (tokenErr) {
          // Fallback: check Firestore role
          const userRef = doc(db, `users/${user.uid}`);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            isSuperAdmin = userData.role === 'superadmin';
          }
        }

        if (isSuperAdmin) {
          setIsApproved(true);
          setLoading(false);
          return;
        }

        // For non-superadmins, check beta approval
        const userRef = doc(db, `users/${user.uid}`);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const approved = userData.betaApproved === true;
          setIsApproved(approved);
        } else {
          setIsApproved(false);
        }
      } catch (err) {
        console.error('Error checking beta approval', err);
        setIsApproved(false);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  if (loading) return null;

  if (!isAuth) {
    // Redirect to login first, then they'll come back here after login
    return <Navigate to="/login" replace state={{ returnTo: window.location.pathname }} />;
  }

  if (!isApproved) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(ellipse at center, rgba(176, 133, 226, 0.08), #0d0d0d)',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: '600px',
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '18px',
          padding: '3rem 2rem',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.35)'
        }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#ffffff' }}>
            Private Beta Access Required
          </h1>
          <p style={{ fontSize: '1.1rem', color: '#c7c7c7', marginBottom: '2rem', lineHeight: '1.6' }}>
            TheBox is currently in private beta. To create or join a team, you need to be approved through our Discord community first.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
            <button
              onClick={() => window.open(DISCORD_INVITE_URL, '_blank')}
              style={{
                background: 'linear-gradient(135deg, #7e57c2, #b085e2)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                padding: '0.85rem 2rem',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 10px 30px rgba(126, 87, 194, 0.35)',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 12px 35px rgba(126, 87, 194, 0.45)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 10px 30px rgba(126, 87, 194, 0.35)';
              }}
            >
              Join Discord to Get Approved
            </button>
            <button
              onClick={async () => {
                await signOut(auth);
                navigate('/login', { state: { returnTo: window.location.pathname } });
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                color: '#c7c7c7',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '12px',
                padding: '0.65rem 1.5rem',
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.12)';
                e.target.style.color = '#ffffff';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                e.target.style.color = '#c7c7c7';
              }}
            >
              Login as Different User
            </button>
            <button
              onClick={() => window.location.href = '/landing'}
              style={{
                background: 'transparent',
                color: '#c7c7c7',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '12px',
                padding: '0.65rem 1.5rem',
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                e.target.style.color = '#ffffff';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#c7c7c7';
              }}
            >
              Back to Home
            </button>
          </div>
          <p style={{ marginTop: '2rem', fontSize: '0.9rem', color: '#aaaaaa', fontStyle: 'italic' }}>
            After joining Discord, introduce your team and we'll approve your access.
          </p>
        </div>
      </div>
    );
  }

  return children;
};

export default BetaApprovedRoute;

